import React, { Fragment } from "react";
import { Field, FieldProps, Form, FormikProps } from "formik";
import classnames from "classnames";
import { WithTranslation, withTranslation } from "react-i18next";

import { AddApplicationSubscriptionFormValues } from "./AddSubscription";

import TextField from "@material-ui/core/TextField";
import IconButton from "@material-ui/core/IconButton";
import Button from "@material-ui/core/Button";

import IconClose from "@material-ui/icons/Close";

import Stepper from "components/common/stepper/Stepper";
import DatePicker from "react-datepicker";
import { MenuItem, Select } from "@material-ui/core";

const AddSubscription = ({
  formikProps,
  formikProps: { errors, touched, isSubmitting, setFieldValue },
  close,
  t
}: {
  formikProps: FormikProps<AddApplicationSubscriptionFormValues>;
  close: (event: any) => void;
} & WithTranslation) => {
  return (
    <Form className="d-flex flex-column h-100">
      <div className="d-flex flex-column h-100">
        <div
          className="d-flex py-2 px-3 justify-content-between align-items-center"
          style={{ borderBottom: "1px solid #dadce0" }}
        >
          <IconButton onClick={close}>
            <IconClose fontSize="small" />
          </IconButton>
          <Button
            type="submit"
            color="secondary"
            variant="contained"
            size="small"
            className="p-2"
            disabled={!formikProps.isValid || isSubmitting}
          >
            {t("application.addSubscription.addButton")}
          </Button>
        </div>
        <div
          className="flex-grow-1 py-4 px-5 overflow-auto"
          style={{ backgroundColor: "rgb(241, 243, 244)" }}
        >
          <Stepper
            titles={t("application.addSubscription.stepper.titles", {
              returnObjects: true
            })}
            steps={[
              <Fragment>
                <section className="box rounded p-3 mb-2">
                  <p className="box__description mb-1">
                    {t("application.addSubscription.fields.startDate.label")}
                  </p>
                  <section className="d-flex flex-column">
                    <Field
                      name="startDate"
                      render={({
                        field
                      }: FieldProps<AddApplicationSubscriptionFormValues>) => (
                        <DatePicker
                          id={field.name}
                          dateFormat="dd-MM-yyyy"
                          selected={field.value}
                          onChange={date => setFieldValue(field.name, date)}
                        />
                      )}
                    />
                    {errors.startDate && (
                      <div className="d-flex invalid-feedback">
                        {t(
                          "application.addSubscription.fields.startDate.error"
                        )}
                      </div>
                    )}
                  </section>
                </section>
                <section className="box rounded p-3 mb-2">
                  <p className="box__description mb-1">
                    {t("application.addSubscription.fields.endDate.label")}
                  </p>
                  <section className="d-flex flex-column">
                    <Field
                      name="endDate"
                      render={({
                        field
                      }: FieldProps<AddApplicationSubscriptionFormValues>) => (
                        <DatePicker
                          id={field.name}
                          dateFormat="dd-MM-yyyy"
                          selected={field.value}
                          onChange={date => setFieldValue(field.name, date)}
                        />
                      )}
                    />
                    {errors.endDate && (
                      <div className="d-flex invalid-feedback">
                        {t("application.addSubscription.fields.endDate.error")}
                      </div>
                    )}
                  </section>
                </section>
                <section className="box rounded p-3 mb-2">
                  <p className="box__description mb-1">
                    {t("application.addSubscription.fields.product.label")}
                  </p>
                  <section className="d-flex flex-column">
                    <Field
                      name="product"
                      render={({
                        field
                      }: FieldProps<AddApplicationSubscriptionFormValues>) => (
                        <Select
                          id={field.name}
                          value={field.value}
                          onChange={event =>
                            setFieldValue(field.name, event.target.value)
                          }
                        >
                          {["onboarding", "development", "exit"].map(option => (
                            <MenuItem key={option} value={option}>
                              {option}
                            </MenuItem>
                          ))}
                        </Select>
                      )}
                    />
                    {errors.product && touched.product && (
                      <div className="d-flex invalid-feedback">
                        {t("application.addSubscription.fields.product.error")}
                      </div>
                    )}
                  </section>
                </section>
                <section className="box rounded p-3 mb-2">
                  <p className="box__description mb-1">
                    {t("application.addSubscription.fields.credits.label")}
                  </p>
                  <section className="d-flex flex-column">
                    <Field
                      name="credits"
                      render={({
                        field
                      }: FieldProps<AddApplicationSubscriptionFormValues>) => (
                        <TextField
                          {...field}
                          inputProps={{
                            className: "px-2",
                            type: "number"
                          }}
                          className={classnames("bg-white m-0", {
                            "is-valid": !errors.credits && touched.credits,
                            "is-invalid": errors.credits && touched.credits
                          })}
                          tabIndex={3}
                        />
                      )}
                    />
                    {errors.credits && touched.credits && (
                      <div className="d-flex invalid-feedback">
                        {t("application.addSubscription.fields.credits.error")}
                      </div>
                    )}
                  </section>
                </section>
                <section className="box rounded p-3 mb-2">
                  <p className="box__description mb-1">
                    {t("application.addSubscription.fields.creditPrice.label")}
                  </p>
                  <section className="d-flex flex-column">
                    <Field
                      name="creditPrice"
                      render={({
                        field
                      }: FieldProps<AddApplicationSubscriptionFormValues>) => (
                        <TextField
                          {...field}
                          inputProps={{
                            className: "px-2",
                            type: "number",
                            step: 0.01
                          }}
                          className={classnames("bg-white m-0", {
                            "is-valid":
                              !errors.creditPrice && touched.creditPrice,
                            "is-invalid":
                              errors.creditPrice && touched.creditPrice
                          })}
                          tabIndex={3}
                        />
                      )}
                    />
                    {errors.creditPrice && touched.creditPrice && (
                      <div className="d-flex invalid-feedback">
                        {t(
                          "application.addSubscription.fields.creditPrice.error"
                        )}
                      </div>
                    )}
                  </section>
                </section>
              </Fragment>
            ]}
          />
        </div>
      </div>
    </Form>
  );
};

export default withTranslation()(AddSubscription);
